import React, { useState, useEffect } from 'react';
import { Star, CheckCircle, XCircle, Trash2, Clock, ArrowLeft, MessageSquare, RefreshCw } from 'lucide-react';
import { schoolSentiment } from '../lib/supabase';

const RATING_FIELDS = [
  ['overall_rating', 'Overall'],
  ['academics_rating', 'Academics'],
  ['facilities_rating', 'Facilities'],
  ['safety_rating', 'Safety'],
  ['extracurricular_rating', 'Extracurricular'],
  ['teacher_quality_rating', 'Teachers'],
];

const Stars = ({ value }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${value || 0} out of 5`}>
    {[1, 2, 3, 4, 5].map(n => (
      <Star key={n} className={`w-3.5 h-3.5 ${n <= (value || 0) ? 'text-amber-500 fill-current' : 'text-gray-300'}`} />
    ))}
  </span>
);

export const AdminSchoolReviewsPage = ({ setPage }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending | approved | all
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await schoolSentiment.getAllAdmin();
    setReviews(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const pendingCount = reviews.filter(r => !r.is_approved).length;
  const filtered = reviews.filter(r =>
    filter === 'all' ? true : filter === 'pending' ? !r.is_approved : r.is_approved
  );

  const act = async (id, fn) => {
    setBusy(id);
    try { await fn(id); await load(); } catch (e) { console.error(e); }
    setBusy(null);
  };

  const tabs = [
    { id: 'pending', label: `Pending${pendingCount ? ` (${pendingCount})` : ''}` },
    { id: 'approved', label: 'Approved' },
    { id: 'all', label: 'All' },
  ];

  return (
    <div className="w-full max-w-4xl mx-auto">
      <button onClick={() => setPage('admin')} className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-800">
        <ArrowLeft className="w-5 h-5" /> Back to Admin
      </button>

      <div className="bg-gradient-to-br from-brand-500 to-brand-600 rounded-2xl p-8 text-white mb-6">
        <MessageSquare className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">School Reviews</h1>
        <p className="text-brand-100">Approve or reject community reviews before they appear publicly.</p>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${filter === t.id ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button onClick={load} className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600" aria-label="Refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600 mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-gray-500">
          <CheckCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="font-medium">No {filter === 'all' ? '' : filter} reviews</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(r => (
            <div key={r.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <h3 className="font-bold text-gray-800">{r.school_name || r.school_id}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {(r.user_type || 'anonymous')}{r.created_at ? ` · ${new Date(r.created_at).toLocaleDateString()}` : ''}
                  </p>
                </div>
                <span className={`px-2 py-1 text-[11px] font-medium rounded-full flex items-center gap-1 ${r.is_approved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                  {r.is_approved ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                  {r.is_approved ? 'Approved' : 'Pending'}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                {RATING_FIELDS.filter(([f]) => r[f] != null).map(([f, label]) => (
                  <div key={f} className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{label}</span>
                    <Stars value={r[f]} />
                  </div>
                ))}
              </div>

              {(r.pros || r.cons || r.advice) && (
                <div className="space-y-1.5 text-sm text-gray-700 border-t pt-3">
                  {r.pros && <p><span className="font-medium text-green-700">Pros:</span> {r.pros}</p>}
                  {r.cons && <p><span className="font-medium text-red-700">Cons:</span> {r.cons}</p>}
                  {r.advice && <p><span className="font-medium text-blue-700">Advice:</span> {r.advice}</p>}
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-4">
                {!r.is_approved && (
                  <button
                    onClick={() => act(r.id, schoolSentiment.approve)}
                    disabled={busy === r.id}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                  >
                    <CheckCircle className="w-4 h-4" /> Approve
                  </button>
                )}
                {r.is_approved && (
                  <button
                    onClick={() => act(r.id, schoolSentiment.reject)}
                    disabled={busy === r.id}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-100 text-amber-700 text-sm font-medium hover:bg-amber-200 disabled:opacity-50"
                  >
                    <XCircle className="w-4 h-4" /> Unpublish
                  </button>
                )}
                <button
                  onClick={() => { if (window.confirm('Delete this review permanently?')) act(r.id, schoolSentiment.delete); }}
                  disabled={busy === r.id}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm font-medium hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSchoolReviewsPage;
